import { createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import { CommonErrorType } from "Reducers/types";

import getGitBlobContent from "./thunk/getGitBlobContent.thunk";
import getGitCommit from "./thunk/getGitCommit.thunk";
import getGitTree from "./thunk/getGitTree.thunk";
import loadConfigFile from "./thunk/loadConfigFile.thunk";

import type { ConfigFile } from "./thunk/loadConfigFile.thunk";
import type { Tree, Commit } from "@packages/client";

type GitStateType<D> = {
  data?: D;
  error?: CommonErrorType;
  isLoading?: boolean;
  subscriberCount: number;
};

type InitGitStateType = {
  tree: {
    [id: string]: GitStateType<Tree>;
  };
  blob: {
    [id: string]: GitStateType<string>;
  };
  commit: { [id: string]: GitStateType<Commit> };
  /** Resolved env config file (`<configDir>/config.yaml`), keyed `projectId:commit`. */
  configFile: { [id: string]: GitStateType<ConfigFile> };
};

const initialState: InitGitStateType = {
  tree: {},
  blob: {},
  commit: {},
  configFile: {}
};

/** Cache key for a resolved env config file. */
export const configFileKey = (projectId?: string, envHeadCommit?: string) =>
  `${projectId ?? ""}:${envHeadCommit ?? ""}`;

// Create a slice
const gitSlice = createSlice({
  name: "git",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Reducer for getGitCommit
      .addCase(getGitCommit.pending, (state, action) => {
        const { arg } = action.meta;
        if (!arg.envHeadCommit) return;
        setDeep(state, ["commit", arg.envHeadCommit, "error"], undefined);
        setDeep(state, ["commit", arg.envHeadCommit, "isLoading"], true);
      })
      .addCase(getGitCommit.fulfilled, (state, action) => {
        const { arg } = action.meta;
        if (!arg.envHeadCommit) return;
        setDeep(state, ["commit", arg.envHeadCommit, "isLoading"], false);
        setDeep(state, ["commit", arg.envHeadCommit, "data"], action.payload);
      })
      .addCase(getGitCommit.rejected, (state, action) => {
        const { arg } = action.meta;
        if (!arg.envHeadCommit) return;
        setDeep(
          state,
          ["commit", arg.envHeadCommit, "error"],
          action.payload.error
        );
        setDeep(state, ["commit", arg.envHeadCommit, "isLoading"], false);
      })
      // Reducer for getGitTree
      .addCase(getGitTree.pending, (state, action) => {
        const { arg } = action.meta;
        if (!arg.commitId) return;
        setDeep(state, ["tree", arg.commitId, "error"], undefined);
        setDeep(state, ["tree", arg.commitId, "isLoading"], true);
      })
      .addCase(getGitTree.fulfilled, (state, action) => {
        const { arg } = action.meta;
        if (!arg.commitId) return;
        setDeep(state, ["tree", arg.commitId, "isLoading"], false);
        setDeep(state, ["tree", arg.commitId, "data"], action.payload);
      })
      .addCase(getGitTree.rejected, (state, action) => {
        const { arg } = action.meta;
        if (!arg.commitId) return;
        setDeep(state, ["tree", arg.commitId, "error"], action.payload.error);
        setDeep(state, ["tree", arg.commitId, "isLoading"], false);
      })

      // Reducer for getGitBlobContent
      .addCase(getGitBlobContent.pending, (state, action) => {
        const { arg } = action.meta;
        const sha = arg?.sha;
        if (!sha) return;
        setDeep(state, ["blob", sha, "error"], undefined);
        setDeep(state, ["blob", sha, "isLoading"], true);
      })
      .addCase(getGitBlobContent.fulfilled, (state, action) => {
        const { arg } = action.meta;
        const sha = arg?.sha;
        if (!sha) return;
        setDeep(state, ["blob", sha, "isLoading"], false);
        setDeep(state, ["blob", sha, "data"], action.payload);
      })
      .addCase(getGitBlobContent.rejected, (state, action) => {
        const { arg } = action.meta;
        const sha = arg?.sha;
        if (!sha) return;
        setDeep(state, ["blob", sha, "error"], action.payload.error);
        setDeep(state, ["blob", sha, "isLoading"], false);
      })

      // Reducer for loadConfigFile
      .addCase(loadConfigFile.pending, (state, action) => {
        const { projectId, envHeadCommit } = action.meta.arg;
        const key = configFileKey(projectId, envHeadCommit);
        setDeep(state, ["configFile", key, "error"], undefined);
        setDeep(state, ["configFile", key, "isLoading"], true);
      })
      .addCase(loadConfigFile.fulfilled, (state, action) => {
        const { projectId, envHeadCommit } = action.meta.arg;
        const key = configFileKey(projectId, envHeadCommit);
        setDeep(state, ["configFile", key, "isLoading"], false);
        setDeep(state, ["configFile", key, "data"], action.payload);
      })
      .addCase(loadConfigFile.rejected, (state, action) => {
        const { projectId, envHeadCommit } = action.meta.arg;
        const key = configFileKey(projectId, envHeadCommit);
        setDeep(state, ["configFile", key, "error"], action.payload?.error);
        setDeep(state, ["configFile", key, "isLoading"], false);
      });
  }
});

export default gitSlice;
